import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function Field({ id, label, required, children, help }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-sm font-medium text-[color:var(--gsf-charcoal)]">
                {label} {required && <span className="text-[color:var(--gsf-red)]">*</span>}
            </Label>
            {children}
            {help && <p className="text-xs text-gray-500">{help}</p>}
        </div>
    );
}

export function TextInput({ id, testid, value, onChange, type = "text", ...rest }) {
    return (
        <Input
            id={id}
            type={type}
            data-testid={testid}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-lg border-gray-200 focus-visible:ring-[color:var(--gsf-green-700)]"
            {...rest}
        />
    );
}

export function TextArea({ id, testid, value, onChange, rows = 4, ...rest }) {
    return (
        <Textarea
            id={id}
            data-testid={testid}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            className="rounded-lg border-gray-200 focus-visible:ring-[color:var(--gsf-green-700)]"
            {...rest}
        />
    );
}

export function SelectField({ id, testid, value, onChange, placeholder, options }) {
    return (
        <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger data-testid={testid} className="rounded-lg border-gray-200">
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {options.map((o) => (
                    <SelectItem key={o.value || o} value={o.value || o} data-testid={`${testid}-opt-${o.value || o}`}>
                        {o.label || o}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

export function ConsentCheckbox({ checked, onChange, testid = "consent-checkbox", text }) {
    return (
        <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
                checked={!!checked}
                onCheckedChange={(v) => onChange(!!v)}
                data-testid={testid}
                className="mt-0.5 border-gray-400 data-[state=checked]:bg-[color:var(--gsf-green-700)] data-[state=checked]:border-[color:var(--gsf-green-700)]"
            />
            <span className="text-xs text-gray-600 leading-relaxed">
                {text || (
                    <>
                        I consent to Global Seed Foundation processing the information I provide for the
                        purposes of this enquiry. My information will be handled in line with the{" "}
                        <a href="/privacy" className="underline text-[color:var(--gsf-green-700)]">Privacy Policy</a>.
                    </>
                )}
            </span>
        </label>
    );
}

// Honeypot — screen-reader-visible but hidden from users
export function Honeypot({ value, onChange }) {
    return (
        <div style={{ position: "absolute", left: "-9999px", top: "auto", width: 1, height: 1, overflow: "hidden" }} aria-hidden>
            <label>Website (leave empty)
                <input type="text" name="website" value={value || ""} onChange={(e) => onChange(e.target.value)} tabIndex={-1} autoComplete="off" />
            </label>
        </div>
    );
}
